import m from 'mithril';

export type Theme = 'day' | 'night' | 'ocean' | 'forest' | 'sunset' | 'rose' | 'purple' | 'red' | 'indigo' | 'gold' | 'blue' | 'desert' | 'dracula' | 'nord' | 'monokai' | 'synthwave' | 'cyberpunk' | 'hacker' | 'valentine' | 'aqua' | 'magma' | 'coffee' | 'neon' | 'midnightruby' | 'matrix' | 'solarflare' | 'abyss' | 'retrowave' | 'paper';

export let currentTheme: Theme = 'day';

export const setTheme = (theme: Theme) => {
    currentTheme = theme;
    if (typeof document !== 'undefined') {
        localStorage.setItem('app-theme', theme);
        document.documentElement.setAttribute('data-theme', theme);
        document.body.className = `theme-${theme}`;
    }
    m.redraw();
};

export type FontStyle = 'sans' | 'serif' | 'mono' | 'roboto' | 'montserrat' | 'poppins' | 'playfair' | 'firacode' | 'cairo' | 'tajawal' | 'plex' | 'almarai' | 'changa' | 'reemkufi' | 'alexandria' | 'readex' | 'lalezar' | 'amiri' | 'aref' | 'katibeh' | 'cinzel' | 'manrope' | 'spacegrotesk' | 'syne' | 'cormorant' | 'lora' | 'nunito' | 'quicksand' | 'notonaskh' | 'zain' | 'marhey';

export let currentFontPrimary: FontStyle = 'sans';
export let currentFontSecondary: FontStyle = 'sans';

const getFontStack = (font: FontStyle): string => {
    switch (font) {
        case 'serif': return 'ui-serif, Georgia, Cambria, "Times New Roman", Times, serif';
        case 'mono': return 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace';
        case 'roboto': return '"Roboto", "Inter", sans-serif';
        case 'montserrat': return '"Montserrat", "Inter", sans-serif';
        case 'poppins': return '"Poppins", "Inter", sans-serif';
        case 'playfair': return '"Playfair Display", serif';
        case 'firacode': return '"Fira Code", monospace';
        case 'cairo': return '"Cairo", "Outfit", "Inter", sans-serif';
        case 'tajawal': return '"Tajawal", "Outfit", "Inter", sans-serif';
        case 'plex': return '"IBM Plex Sans Arabic", "Outfit", "Inter", sans-serif';
        case 'almarai': return '"Almarai", "Inter", sans-serif';
        case 'changa': return '"Changa", "Inter", sans-serif';
        case 'reemkufi': return '"Reem Kufi", "Inter", sans-serif';
        case 'alexandria': return '"Alexandria", "Inter", sans-serif';
        case 'readex': return '"Readex Pro", "Inter", sans-serif';
        case 'lalezar': return '"Lalezar", cursive';
        case 'amiri': return '"Amiri", serif';
        case 'aref': return '"Aref Ruqaa", serif';
        case 'katibeh': return '"Katibeh", cursive';
        case 'cinzel': return '"Cinzel", serif';
        case 'manrope': return '"Manrope", "Inter", sans-serif';
        case 'spacegrotesk': return '"Space Grotesk", "Inter", sans-serif';
        case 'syne': return '"Syne", "Inter", sans-serif';
        case 'cormorant': return '"Cormorant Garamond", serif';
        case 'lora': return '"Lora", serif';
        case 'nunito': return '"Nunito", "Inter", sans-serif';
        case 'quicksand': return '"Quicksand", "Inter", sans-serif';
        case 'notonaskh': return '"Noto Naskh Arabic", serif';
        case 'zain': return '"Zain", "Inter", sans-serif';
        case 'marhey': return '"Marhey", cursive';
        default: return 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';
    }
};

export const setFontPrimary = (font: FontStyle) => {
    currentFontPrimary = font;
    if (typeof document !== 'undefined') {
        localStorage.setItem('app-font-primary', font);
        document.documentElement.style.setProperty('--font-primary', getFontStack(font));
    }
    m.redraw();
};

export const setFontSecondary = (font: FontStyle) => {
    currentFontSecondary = font;
    if (typeof document !== 'undefined') {
        localStorage.setItem('app-font-secondary', font);
        document.documentElement.style.setProperty('--font-secondary', getFontStack(font));
    }
    m.redraw();
};

if (typeof document !== 'undefined') {
    const savedTheme = localStorage.getItem('app-theme');
    setTheme((savedTheme && savedTheme !== 'null' ? savedTheme : 'day') as Theme);
    
    // Migrate old 'app-font' to primary/secondary if it exists and new keys don't
    let savedFontPrimary = localStorage.getItem('app-font-primary');
    let savedFontSecondary = localStorage.getItem('app-font-secondary');
    if (!savedFontPrimary && !savedFontSecondary) {
        const legacyFont = localStorage.getItem('app-font');
        if (legacyFont) {
            savedFontPrimary = legacyFont;
            savedFontSecondary = legacyFont;
        }
    }
    
    setFontPrimary((savedFontPrimary && savedFontPrimary !== 'null' ? savedFontPrimary : 'sans') as FontStyle);
    setFontSecondary((savedFontSecondary && savedFontSecondary !== 'null' ? savedFontSecondary : 'sans') as FontStyle);
}
